import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ReactFlow, Background, Controls, Panel, useNodesState, useEdgesState,
  ConnectionMode, MarkerType, type Node, type Edge, type Connection, type NodeMouseHandler, type EdgeMouseHandler,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { AgentNode, type AgentNodeData } from "./AgentNode";
import { ConnectAgentsDialog } from "./ConnectAgentsDialog";
import { useAgentGraph } from "@/api/hooks/useAgentGraph";
import {
  useRevokeConnection, useAcceptConnectionRequest, useRejectConnectionRequest, useCancelConnectionRequest,
} from "@/api/mutations/connections";
import type { Agent, AgentConnection, AgentConnectionRequest } from "@/types";

const PROBLEM_PHASES = ["crashed", "crashloopbackoff", "degraded"];
const nodeTypes = { agent: AgentNode };

// Deterministische Kreis-Position für erstmals gesehene Nodes.
function circlePos(i: number, n: number): { x: number; y: number } {
  const radius = Math.max(220, n * 46);
  const angle = (i / Math.max(1, n)) * 2 * Math.PI - Math.PI / 2;
  return { x: Math.cos(angle) * radius, y: Math.sin(angle) * radius };
}

function matches(a: Agent, q: string, phase: string): boolean {
  if (q && !`${a.name} ${a.id}`.toLowerCase().includes(q)) return false;
  if (phase === "running" && a.lifecyclePhase !== "running") return false;
  if (phase === "problem" && !PROBLEM_PHASES.includes(a.lifecyclePhase)) return false;
  if (phase === "stopped" && a.lifecyclePhase !== "stopped") return false;
  if (phase === "suspended" && a.lifecyclePhase !== "suspended") return false;
  return true;
}

type EdgeSel =
  | { kind: "connection"; conn: AgentConnection }
  | { kind: "request"; req: AgentConnectionRequest };

export function AgentsGraph({ agents, search, phaseFilter }: { agents: Agent[]; search: string; phaseFilter: string }) {
  const navigate = useNavigate();
  const { connections, requests, isError } = useAgentGraph(agents, true);
  const [showConnected, setShowConnected] = useState(true);

  const agentMap = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);

  // Stabile Signaturen → useMemo feuert nur bei echtem Inhalts-Wechsel (nicht bei jedem Refetch-Ref).
  const agentSig = agents.map((a) => `${a.id}:${a.lifecyclePhase}:${a.revokedAt ? 1 : 0}`).join(",");
  const connSig = connections.map((c) => c.id).sort().join(",");
  const reqSig = requests.map((r) => r.id).sort().join(",");
  const q = search.trim().toLowerCase();
  const filterActive = q.length > 0 || phaseFilter.length > 0;

  // Sichtbare Agents: Treffer + (optional) deren direkte Nachbarn.
  const visibleAgents = useMemo(() => {
    const live = agents.filter((a) => !a.revokedAt);
    if (!filterActive) return live;
    const matched = new Set(live.filter((a) => matches(a, q, phaseFilter)).map((a) => a.id));
    const visible = new Set(matched);
    if (showConnected) {
      const addNeighbor = (x: string, y: string) => { if (matched.has(x)) visible.add(y); if (matched.has(y)) visible.add(x); };
      for (const c of connections) addNeighbor(c.agentAId, c.agentBId);
      for (const r of requests) addNeighbor(r.requesterAgentId, r.targetAgentId);
    }
    return live.filter((a) => visible.has(a.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentSig, connSig, reqSig, q, phaseFilter, showConnected, filterActive]);

  const derivedEdges = useMemo<Edge[]>(() => {
    const vis = new Set(visibleAgents.map((a) => a.id));
    const out: Edge[] = [];
    for (const c of connections) {
      if (!vis.has(c.agentAId) || !vis.has(c.agentBId)) continue;
      out.push({
        id: c.id, source: c.agentAId, target: c.agentBId, type: "default",
        style: { stroke: "var(--accent)", strokeWidth: 2 }, data: { kind: "connection", conn: c },
      });
    }
    for (const r of requests) {
      if (!vis.has(r.requesterAgentId) || !vis.has(r.targetAgentId)) continue;
      out.push({
        id: `req-${r.id}`, source: r.requesterAgentId, target: r.targetAgentId, animated: true,
        style: { stroke: "var(--warn)", strokeWidth: 2, strokeDasharray: "6 4" },
        markerEnd: { type: MarkerType.ArrowClosed, color: "var(--warn)" },
        data: { kind: "request", req: r },
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleAgents, connSig, reqSig]);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node<AgentNodeData>>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Nodes neu aufbauen, wenn sich der sichtbare Satz ändert — gedraggte Positionen erhalten.
  useEffect(() => {
    setNodes((prev) => {
      const prevPos = new Map(prev.map((n) => [n.id, n.position]));
      const n = visibleAgents.length;
      return visibleAgents.map((a, i) => ({
        id: a.id,
        type: "agent",
        position: prevPos.get(a.id) ?? circlePos(i, n),
        data: { agent: a },
      }));
    });
  }, [visibleAgents, setNodes]);

  useEffect(() => { setEdges(derivedEdges); }, [derivedEdges, setEdges]);

  const [pendingConnect, setPendingConnect] = useState<{ source: Agent; target: Agent } | null>(null);
  const onConnect = useCallback((params: Connection) => {
    if (!params.source || !params.target || params.source === params.target) return;
    const s = agentMap.get(params.source);
    const t = agentMap.get(params.target);
    if (s && t) setPendingConnect({ source: s, target: t });
  }, [agentMap]);

  const [edgeSel, setEdgeSel] = useState<EdgeSel | null>(null);
  const onEdgeClick = useCallback<EdgeMouseHandler>((_e, edge) => {
    const data = edge.data as EdgeSel | undefined;
    if (data) setEdgeSel(data);
  }, []);

  const onNodeDoubleClick = useCallback<NodeMouseHandler>((_e, node) => {
    navigate(`/agents/${node.id}`);
  }, [navigate]);

  return (
    <div className="h-[72vh] w-full overflow-hidden rounded-lg border border-border bg-surface" data-testid="agents-graph">
      {isError && (
        <div className="border-b border-border bg-danger-bg px-4 py-2 text-[12.5px] text-danger">
          Connections konnten nicht vollständig geladen werden — Graph evtl. unvollständig.
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={onEdgeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        nodeTypes={nodeTypes}
        connectionMode={ConnectionMode.Loose}
        fitView
        minZoom={0.2}
        proOptions={{ hideAttribution: true }}
      >
        <Background color="var(--border-strong)" gap={22} />
        <Controls className="!rounded-md !border !border-border !bg-surface !shadow-sm" showInteractive={false} />
        <Panel position="top-left" className="!m-2 flex flex-col gap-2">
          {filterActive && (
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-[12px] text-text2 shadow-sm">
              <input type="checkbox" checked={showConnected} onChange={(e) => setShowConnected(e.target.checked)} className="accent-accent" data-testid="graph-show-connected" />
              Verbundene mitanzeigen
            </label>
          )}
          <div className="flex flex-col gap-1 rounded-md border border-border bg-surface px-2.5 py-2 text-[11px] text-text3 shadow-sm">
            <span className="inline-flex items-center gap-1.5"><span className="h-[2px] w-5 bg-accent" /> aktive Connection</span>
            <span className="inline-flex items-center gap-1.5"><span className="h-0 w-5 border-t-2 border-dashed border-warn" /> pending Request</span>
            <span className="mt-0.5 text-text3">Ziehen zum Verbinden · Doppelklick öffnet Agent</span>
          </div>
        </Panel>
        {nodes.length === 0 && (
          <Panel position="top-center" className="!mt-20">
            <div className="flex items-center gap-2 rounded-md border border-border bg-surface px-4 py-3 text-sm text-text3 shadow-sm">
              <Bot className="h-4 w-4" /> {filterActive ? "Kein Agent passt zum Filter" : "Keine Agents"}
            </div>
          </Panel>
        )}
      </ReactFlow>

      {pendingConnect && (
        <ConnectAgentsDialog
          open
          onOpenChange={(o) => !o && setPendingConnect(null)}
          source={pendingConnect.source}
          target={pendingConnect.target}
        />
      )}

      {edgeSel && (
        <EdgeActionDialog sel={edgeSel} agentMap={agentMap} onClose={() => setEdgeSel(null)} />
      )}
    </div>
  );
}

// ---- Edge-Aktionen (Revoke / Accept / Reject / Cancel) ----
function EdgeActionDialog({
  sel,
  agentMap,
  onClose,
}: {
  sel: EdgeSel;
  agentMap: Map<string, Agent>;
  onClose: () => void;
}) {
  const revokeM = useRevokeConnection();
  const acceptM = useAcceptConnectionRequest();
  const rejectM = useRejectConnectionRequest();
  const cancelM = useCancelConnectionRequest();
  const busy = revokeM.isPending || acceptM.isPending || rejectM.isPending || cancelM.isPending;
  const name = (id: string) => agentMap.get(id)?.name ?? id;

  function done(verb: string) {
    return {
      onSuccess: () => { toast.success(verb); onClose(); },
      onError: (err: Error) => toast.error(`${verb} fehlgeschlagen: ${err.message}`),
    };
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edge-action-dialog">
        <DialogHeader>
          <DialogTitle>{sel.kind === "connection" ? "Aktive Connection" : "Pending Request"}</DialogTitle>
          <DialogDescription>
            {sel.kind === "connection"
              ? "Aktive A2A-Verbindung — beide Agents können sich gegenseitig adressieren."
              : "Angefragte Verbindung — wartet auf Bestätigung durch das Ziel."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface2 px-3.5 py-2.5 text-[13px]">
          {sel.kind === "connection" ? (
            <>
              <span className="mono font-semibold text-text">{name(sel.conn.agentAId)}</span>
              <Badge variant="online">aktiv</Badge>
              <span className="mono font-semibold text-text">{name(sel.conn.agentBId)}</span>
            </>
          ) : (
            <>
              <span className="mono font-semibold text-text">{name(sel.req.requesterAgentId)}</span>
              <Badge variant="busy">pending →</Badge>
              <span className="mono font-semibold text-text">{name(sel.req.targetAgentId)}</span>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Schließen</Button>
          {sel.kind === "connection" ? (
            <Button
              variant="destructive"
              disabled={busy}
              data-testid="edge-revoke"
              onClick={() => revokeM.mutate(
                { connectionId: sel.conn.id, agentAId: sel.conn.agentAId, agentBId: sel.conn.agentBId },
                done("Connection widerrufen"),
              )}
            >
              Revoke
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                disabled={busy}
                data-testid="edge-cancel"
                onClick={() => cancelM.mutate(
                  { requestId: sel.req.id, requesterAgentId: sel.req.requesterAgentId, targetAgentId: sel.req.targetAgentId },
                  done("Request abgebrochen"),
                )}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={busy}
                data-testid="edge-reject"
                onClick={() => rejectM.mutate(sel.req.id, done("Request abgelehnt"))}
              >
                Reject
              </Button>
              <Button
                disabled={busy}
                data-testid="edge-accept"
                onClick={() => acceptM.mutate(sel.req.id, done("Request akzeptiert"))}
              >
                Accept
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
